import Heading from '@pelckmans/elementis/components/heading';
import Paragraph from '@pelckmans/elementis/components/paragraph';
import UploadField from '@pelckmans/elementis/components/upload-field';
import { AxiosRequestConfig } from 'axios';
import { useCallback, useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ErrorResponseData, handleUpload } from '../api';
import { ErrorFields, ImportMembersContext } from '../contexts/ImportMembersContext';

const TRANSLATION_KEY = 'PAGES.MY_GROUPS.MODALS.IMPORT_MEMBERS.FILE_IMPORT';

const IMPORT_TEMPLATE_URL = 'https://pakket-p-public.s3.eu-west-1.amazonaws.com/code-based/import_template.xlsx';
const FILE_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const ACCEPT_TYPES = '.xlsx';

function getErrorMessageKey(error: any) {
  const message = error?.response?.data?.message;
  switch (message) {
    case 'File too large':
      return 'FILE_SIZE_EXCEEDED';
    case 'Only .xlsx files are allowed':
      return 'FILE_TYPE_NOT_SUPPORTED';
    case 'Maximum 100 records allowed':
      return 'FILE_RECORDS_EXCEEDED';
    case 'No data found in the file':
      return 'FILE_NO_DATA_FOUND';
    default:
      return 'FILE_UPLOAD_FAILED';
  }
}

export default function MembersFileImport() {
  const { t } = useTranslation();

  const { setFileData, fileName, setFileName, setErrors, errors } = useContext(ImportMembersContext);

  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<AbortController | null>(null);

  const setFileImportError = useCallback(
    (message: string | undefined) => {
      setErrors({ [ErrorFields.UPLOAD_FIELD]: message });
    },
    [setErrors],
  );

  async function onFileSelected(file: File) {
    setFileName(file.name);
    setFileImportError(undefined);
    setProgress(0);
    setShowProgress(true);

    if (!file || file?.type !== FILE_TYPE) {
      setFileImportError(t(`${TRANSLATION_KEY}.UPLOAD_FIELD.ERRORS.FILE_TYPE_NOT_SUPPORTED`));
      return;
    }

    try {
      const abortController = new AbortController();
      setCurrentRequest(abortController);

      const axiosConfig: AxiosRequestConfig = {
        onUploadProgress: function (progressEvent) {
          // Upload percentage max 98% for catching buffer bloat issue (depending on user hardware)
          const percentCompleted = Math.min(
            98,
            Math.round((progressEvent.loaded * 100) / (progressEvent.total || 100)),
          );
          setProgress(percentCompleted);
        },
        signal: abortController.signal,
      };
      const data = await handleUpload(file, axiosConfig);
      setFileData(data);

      setProgress(100);
    } catch (error: any) {
      setShowProgress(false);
      setProgress(0);

      const messageKey = getErrorMessageKey(error);
      if (messageKey === 'FILE_SIZE_EXCEEDED') {
        const { details } = error?.response?.data as ErrorResponseData;
        const maxSizeInMb = Math.floor((details.maxFileSize || 0) / 1024 / 1024);
        setFileImportError(t(`${TRANSLATION_KEY}.UPLOAD_FIELD.ERRORS.${messageKey}`, { size: maxSizeInMb }));
      } else {
        setFileImportError(t(`${TRANSLATION_KEY}.UPLOAD_FIELD.ERRORS.${messageKey}`));
      }
    } finally {
      setCurrentRequest(null);
    }
  }

  function onClear() {
    if (currentRequest) {
      currentRequest.abort();
      setCurrentRequest(null);
    } else {
      setFileName(undefined);
      setFileData(undefined);
    }

    setShowProgress(false);
    setProgress(0);
  }

  return (
    <>
      <div className="modal-content-text">
        <Heading level={4} removeSpacing="top" className="modal-content-text-title">
          {t(`${TRANSLATION_KEY}.TITLE`)}
        </Heading>
        <Paragraph size="md" className="modal-content-text-body">
          <Trans
            i18nKey={`${TRANSLATION_KEY}.DESCRIPTION`}
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            components={{ a: <a href={IMPORT_TEMPLATE_URL} target="_blank" rel="noopener noreferrer" /> }}
          />
        </Paragraph>
      </div>
      <UploadField
        id="file"
        key="file"
        labelText={t(`${TRANSLATION_KEY}.UPLOAD_FIELD.LABEL`)}
        placeholder={t(`${TRANSLATION_KEY}.UPLOAD_FIELD.PLACEHOLDER`)}
        onFileSelected={onFileSelected}
        onClear={onClear}
        helpText={t(`${TRANSLATION_KEY}.UPLOAD_FIELD.HELPER_TEXT`)}
        required
        error={Boolean(errors?.[ErrorFields.UPLOAD_FIELD])}
        errorText={errors?.[ErrorFields.UPLOAD_FIELD]}
        showProgress={showProgress}
        progress={progress}
        value={fileName || ''}
        accept={ACCEPT_TYPES}
      />
    </>
  );
}
